import { rgba } from 'polished';
import { PropsWithChildren, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import {
	Button,
	FlexGap,
	GearIcon,
	IconButton,
	Input,
	JunkIcon,
	Label,
	Paragraph,
	Popover,
	ReorderIcon,
	tokens,
} from '@treams/treams-ui';

const Card = styled.div<{
	dragging: boolean;
}>`
	background: ${tokens.surface.secondary};
	border-radius: 5px;

	${(props) =>
		props.dragging &&
		`
	box-shadow: 0px 0px 0px 1px ${rgba(tokens.text.secondary, 0.1)},
			    0px 4px 8px ${rgba(tokens.text.secondary, 0.25)},
			    0px 32px 64px ${rgba(tokens.text.secondary, 0.25)};
	`}
`;

const Header = styled(Flex)`
	padding: 10px 19px;
	background: white;
	border: 1px solid #c2cad2;
	border-top-left-radius: 6px;
	border-top-right-radius: 6px;
`;

const Title = styled(Paragraph)`
	color: ${tokens.text.secondary};
	font-size: 11px;
	text-transform: uppercase;
	font-weight: bold;
	flex-grow: 1;
`;

function SectionWeight(props: {
	weight?: number;
	error?: string;
	onChange: (weight?: number) => void;
}) {
	const { t } = useTranslation();
	const [visible, setVisible] = useState(false);

	return (
		<Popover.Target
			visible={visible}
			horizontalAlign="right"
			onDismiss={() => setVisible(false)}
			contents={
				<FlexGap flexDirection="column" gap={tokens.gap.element} p={3} width={220}>
					<Label>{t('common.settings.evaluationForms.section.weight.label')}</Label>
					<Input
						type="number"
						value={props.weight === undefined ? '' : String(props.weight)}
						placeholder={t('common.settings.evaluationForms.section.weight.neutral')}
						suffix="%"
						inputProps={{ min: 0, max: 100, step: 1 }}
						error={props.error}
						clearable
						onClear={() => props.onChange(undefined)}
						onChange={(value) => {
							if (value === '') {
								props.onChange(undefined);
								return;
							}

							const weight = Number(value);

							if (!Number.isNaN(weight)) {
								props.onChange(Math.min(100, Math.max(0, weight)));
							}
						}}
					/>
				</FlexGap>
			}
		>
			{props.weight === undefined ? (
				<IconButton
					icon={<GearIcon />}
					type="ghost"
					title={t('common.settings.evaluationForms.section.weight.label')}
					onClick={() => setVisible(true)}
				/>
			) : (
				<Button
					small
					disableSuccess
					icon={<GearIcon />}
					title={`${props.weight}%`}
					type={props.error ? 'destructive' : 'tertiary'}
					onClick={() => setVisible(true)}
				/>
			)}
		</Popover.Target>
	);
}

export default function SectionCard(
	props: PropsWithChildren<{
		id: string;
		section: number;
		dragHandleProps?: DraggableProvided['dragHandleProps'];
		dragging: boolean;
		onDelete: (id: string) => void;
		weight?: {
			value?: number;
			error?: string;
			onChange: (weight?: number) => void;
		};
	}>,
) {
	const { t } = useTranslation();

	return (
		<Card
			dragging={props.dragging}
			onMouseDown={() => (document.activeElement as HTMLElement)?.blur()}
		>
			<Header alignItems="center">
				<Title>
					{t('common.settings.evaluationForms.section.title')} #{props.section + 1}
				</Title>
				<FlexGap alignItems="center" gap={tokens.gap.element}>
					{props.weight && (
						<SectionWeight
							weight={props.weight.value}
							error={props.weight.error}
							onChange={props.weight.onChange}
						/>
					)}
					<IconButton
						icon={<JunkIcon />}
						type="destructive"
						onClick={() => props.onDelete(props.id)}
					/>
					<IconButton icon={<ReorderIcon />} type="ghost" {...props.dragHandleProps} />
				</FlexGap>
			</Header>
			{props.children}
		</Card>
	);
}
